import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import './RecordingArchivePage.css';
import './RecordingLibraryPage.css';

const LABELS = {
  ru: {
    caption: 'Кабинет проводника', title: 'Записи игр',
    description: 'Все доступные записи групповых игр в одном месте. Просматривайте сессии, управляйте ссылками и создавайте MP4 по запросу.',
    filter: 'Статус', all: 'Все записи', completed: 'Завершённые', recording: 'Идёт запись',
    loading: 'Загружаем записи…', empty: 'Записей пока нет.',
    failed: 'Не удалось загрузить архив', retry: 'Повторить',
    game: 'Игра', started: 'Начало записи', stopped: 'Завершена', expires: 'Хранится до',
    view: 'Смотреть', shares: 'Поделиться', mp4: 'MP4', remove: 'Удалить',
    removeConfirm: 'Безвозвратно удалить запись, аудио, события и все ссылки на неё?',
    removeFailed: 'Не удалось удалить запись. Убедитесь, что экспорт MP4 завершён.',
    pagination: 'Страница', of: 'из', previous: 'Назад', next: 'Далее',
    count: 'Записей', pageSize: 'На странице', deletedGame: 'Удалённая игра',
    statusActive: 'Записывается', statusCompleted: 'Завершена',
    noAudio: 'Без аудио', audio: 'Аудио',
  },
  ua: {
    caption: 'Кабінет провідника', title: 'Записи ігор',
    description: 'Усі доступні записи групових ігор в одному місці. Переглядайте сесії, керуйте посиланнями та створюйте MP4 за запитом.',
    filter: 'Статус', all: 'Усі записи', completed: 'Завершені', recording: 'Триває запис',
    loading: 'Завантажуємо записи…', empty: 'Записів поки немає.',
    failed: 'Не вдалося завантажити архів', retry: 'Повторити',
    game: 'Гра', started: 'Початок запису', stopped: 'Завершена', expires: 'Зберігається до',
    view: 'Переглянути', shares: 'Поділитися', mp4: 'MP4', remove: 'Видалити',
    removeConfirm: 'Назавжди видалити запис, аудіо, події та всі посилання?',
    removeFailed: 'Не вдалося видалити запис. Переконайтеся, що експорт MP4 завершено.',
    pagination: 'Сторінка', of: 'з', previous: 'Назад', next: 'Далі',
    count: 'Записів', pageSize: 'На сторінці', deletedGame: 'Видалена гра',
    statusActive: 'Записується', statusCompleted: 'Завершена',
    noAudio: 'Без аудіо', audio: 'Аудіо',
  },
  en: {
    caption: 'Conductor dashboard', title: 'Game recordings',
    description: 'All available group-game recordings in one place. Watch sessions, manage private sharing links and request MP4 exports.',
    filter: 'Status', all: 'All recordings', completed: 'Completed', recording: 'Recording',
    loading: 'Loading recordings…', empty: 'No recordings yet.',
    failed: 'Could not load recording library', retry: 'Retry',
    game: 'Game', started: 'Recording started', stopped: 'Ended', expires: 'Available until',
    view: 'Watch', shares: 'Share', mp4: 'MP4', remove: 'Delete',
    removeConfirm: 'Permanently delete the recording, audio, events and all links?',
    removeFailed: 'Could not delete recording. Make sure MP4 export is complete.',
    pagination: 'Page', of: 'of', previous: 'Previous', next: 'Next',
    count: 'Recordings', pageSize: 'Per page', deletedGame: 'Deleted game',
    statusActive: 'Recording', statusCompleted: 'Completed',
    noAudio: 'No audio', audio: 'Audio',
  },
};

const localizedDate = (date, language) => date
  ? new Date(date).toLocaleString(language === 'ua' ? 'uk-UA' : language === 'ru' ? 'ru-RU' : 'en-US')
  : '—';

export default function RecordingLibraryPage() {
  const { token } = useAuth();
  const { i18n } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const language = i18n.language?.startsWith('ru') ? 'ru'
    : i18n.language?.startsWith('en') ? 'en' : 'ua';
  const labels = LABELS[language];
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteBusy, setDeleteBusy] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!token) throw new Error('AUTH_REQUIRED');
        const params = new URLSearchParams({
          page: String(page), pageSize: String(pageSize), status,
        });
        const response = await fetch(apiUrl('/api/recording-library?' + params), {
          method: 'GET', signal: controller.signal, cache: 'no-store',
          headers: { Authorization: 'Bearer ' + token },
        });
        if (!response.ok) throw new Error('LIBRARY_FAILED');
        const result = await response.json();
        if (cancelled) return;
        setRecords(Array.isArray(result.recordings) ? result.recordings : []);
        setTotal(Number(result.total) || 0);
        setTotalPages(Number(result.totalPages) || 0);
      } catch (cause) {
        if (!cancelled && cause?.name !== 'AbortError') setError(labels.failed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; controller.abort(); };
  }, [page, pageSize, status, token, reload, labels.failed]);

  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };
  const changePageSize = (value) => {
    setPageSize(value);
    setPage(1);
  };

  const deleteRecording = async (recording) => {
    if (deleteBusy || !window.confirm(labels.removeConfirm)) return;
    setDeleteBusy(recording.id);
    setError('');
    try {
      const url = apiUrl('/api/games/' + recording.gameId + '/recordings/' + recording.id);
      const response = await fetch(url, {
        method: 'DELETE', cache: 'no-store',
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!response.ok) throw new Error('DELETE_FAILED');
      // Re-query so counts and pagination are never stale after deletion.
      if (records.length === 1 && page > 1) setPage(page - 1);
      else setReload((value) => value + 1);
    } catch {
      setError(labels.removeFailed);
    } finally {
      setDeleteBusy('');
    }
  };

  return (
    <div className="page-card recording-library">
      <div className="page-card-header">
        <div>
          <p className="page-caption">{labels.caption}</p>
          <h1>{labels.title}</h1>
          <p className="page-description">{labels.description}</p>
        </div>
      </div>

      <section className="recording-library__panel">
        <div className="recording-library__filters">
          <label>{labels.filter}
            <select value={status} onChange={(event) => changeStatus(event.target.value)}>
              <option value="all">{labels.all}</option>
              <option value="completed">{labels.completed}</option>
              <option value="recording">{labels.recording}</option>
            </select>
          </label>
          <label>{labels.pageSize}
            <select value={pageSize} onChange={(event) => changePageSize(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </label>
          {!loading && <span className="replay-muted" aria-live="polite">{labels.count}: {total}</span>}
        </div>

        {loading && <p role="status" className="replay-muted">{labels.loading}</p>}
        {!!error && (
          <div className="recording-library__error" role="alert">
            <span>{error}</span>
            <button type="button" className="replay-secondary"
              onClick={() => setReload((value) => value + 1)}>{labels.retry}</button>
          </div>
        )}
        {!loading && !error && records.length === 0 && (
          <p className="replay-muted">{labels.empty}</p>
        )}

        {!loading && !error && records.length > 0 && (
          <div className="recording-library__records">
            {records.map((recording) => {
              const detail = recording.gameId
                ? '/dashboard/games/' + (recording.gameType === 'individual' ? 'individual' : 'group')
                  + '/' + recording.gameId + '/recordings/' + recording.id
                : '/dashboard/recordings/' + recording.id;
              const completed = recording.status === 'completed';
              return (
                <article key={recording.id} className="recording-library__record">
                  <div className="recording-library__details">
                    <div className="recording-library__heading">
                      <strong>{recording.gameTitle || (recording.gameId
                        ? labels.game + ' #' + recording.gameId : labels.deletedGame)}</strong>
                      <span className={'recording-library__status' + (completed ? ' is-completed' : '')}>
                        {completed ? labels.statusCompleted : labels.statusActive}
                      </span>
                    </div>
                    <div className="recording-library__dates">
                      <span>{labels.started}: {localizedDate(recording.recordingStartedAt, language)}</span>
                      <span>{labels.stopped}: {localizedDate(recording.recordingStoppedAt, language)}</span>
                      <span>{labels.expires}: {localizedDate(recording.expiresAt, language)}</span>
                    </div>
                  </div>
                  <div className="recording-library__actions">
                    <Link className="replay-primary" to={getLocalizedPath(detail)}>
                      {labels.view}
                    </Link>
                    {completed && (
                      <>
                        <Link className="replay-secondary"
                          to={getLocalizedPath(detail) + '#replay-shares'}>{labels.shares}</Link>
                        <Link className="replay-secondary"
                          to={getLocalizedPath(detail) + '#replay-mp4'}>{labels.mp4}</Link>
                        <button className="replay-secondary recording-library__delete" type="button"
                          disabled={Boolean(deleteBusy)} onClick={() => deleteRecording(recording)}>
                          {labels.remove}
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="recording-library__pagination">
            <button type="button" className="replay-secondary"
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              disabled={page <= 1}>{labels.previous}</button>
            <span>{labels.pagination} {page} {labels.of} {totalPages}</span>
            <button type="button" className="replay-secondary"
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              disabled={page >= totalPages}>{labels.next}</button>
          </div>
        )}
      </section>
    </div>
  );
}
